/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { ApolloQueryResult } from 'apollo-client';

import { ActionOverflow } from '@coursera/cds-core';

import type {
  EnterpriseBadgeCollectionsQuery as EnterpriseBadgeCollectionsQueryData,
  EnterpriseBadgeCollectionsQueryVariables,
} from 'bundles/enterprise-learner-onboarding/queries/__generated__/EnterpriseBadgeCollectionsQuery';
import UnenrollConfirmationModal from 'bundles/enterprise-legacy-learner-home/components/enrollmentWidget/UnenrollConfirmationModal';
import SessionSwitchModal from 'bundles/ondemand/components/sessions/SessionSwitchModal';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/course-cards';

const styles = {
  CardDropdownWidget: css`
    position: absolute;
    top: 0;
    z-index: 1;
    right: 0;
  `,
};

export type Props = {
  itemType: 'COURSE' | 'S12N' | 'BADGE';
  itemId: string;
  programId: string;
  enableSwitchSessions?: boolean;
  canUnenroll?: boolean;
  isPartOfS12n?: boolean;
  trackingName: string;
  name: string;
  thirdPartyOrganizationId?: string;
  userId: number;
  refetchEnterpriseBadgeCollections?: (
    variables?: EnterpriseBadgeCollectionsQueryVariables
  ) => Promise<ApolloQueryResult<EnterpriseBadgeCollectionsQueryData>>;
};

/**
 * A simple dropdown dot button for S12nCard and CourseCard
 */
// TODO(Audrey): show session switch, only show unenroll when available (waiting for new api)
const CardDropdownWidget = ({
  itemType,
  itemId,
  programId,
  enableSwitchSessions,
  canUnenroll,
  trackingName,
  name,
  thirdPartyOrganizationId,
  userId,
  refetchEnterpriseBadgeCollections,
}: Props) => {
  const [showUnenrollModal, setShowUnenrollModal] = React.useState<boolean>(false);
  const [showSessionSwitchModal, setShowSessionSwitchModal] = React.useState<boolean>(false);

  const handleCloseUnenrollModal = () => {
    setShowUnenrollModal(false);
  };

  const handleOpenUnenrollModal = () => {
    setShowUnenrollModal(true);
  };

  const handleOpenSessionSwitchModal = () => {
    setShowSessionSwitchModal(true);
  };

  const handleCloseSessionSwitchModal = () => {
    setShowSessionSwitchModal(false);
  };

  const returnFocusRef = React.createRef<HTMLButtonElement>();

  const isCourse = itemType === 'COURSE';
  const productName = name;
  const label = productName && _t('Enrollment actions for #{productName}', { productName });

  let dropdownUnenrollLabel = '';
  switch (itemType) {
    case 'COURSE':
      dropdownUnenrollLabel = _t('Unenroll from Course');
      break;
    case 'S12N':
      dropdownUnenrollLabel = _t('Unenroll from Specialization');
      break;
    case 'BADGE':
      dropdownUnenrollLabel = _t('Unenroll from Badge');
      break;
    default:
  }

  const hasMenuItems = canUnenroll || enableSwitchSessions;

  return (
    <TrackedDiv
      trackingName={trackingName}
      trackClicks={true}
      css={[styles.CardDropdownWidget]}
      data-testid={`CardDropdownWidget~${itemId}`}
    >
      {hasMenuItems && (
        <ActionOverflow aria-label={label || undefined}>
          {canUnenroll && (
            <ActionOverflow.Item onClick={handleOpenUnenrollModal} data-testid={`CardDropdownUnenrollButton~${itemId}`}>
              {dropdownUnenrollLabel}
            </ActionOverflow.Item>
          )}
          {enableSwitchSessions && (
            <ActionOverflow.Item
              onClick={handleOpenSessionSwitchModal}
              data-testid={`CardDropdownSessionSwitchButton~${itemId}`}
            >
              {_t('Switch sessions')}
            </ActionOverflow.Item>
          )}
        </ActionOverflow>
      )}
      {showSessionSwitchModal && (
        // @ts-expect-error ts-migrate(2322) FIXME: Type '{ onClose: () => void; courseId: string; }' ... Remove this comment to see the full error message
        <SessionSwitchModal onClose={handleCloseSessionSwitchModal} courseId={itemId} />
      )}
      <UnenrollConfirmationModal
        itemId={itemId}
        programId={programId}
        thirdPartyOrganizationId={thirdPartyOrganizationId}
        isCourse={isCourse}
        itemType={itemType}
        open={showUnenrollModal}
        name={name}
        userId={userId}
        handleClose={handleCloseUnenrollModal}
        refetchEnterpriseBadgeCollections={refetchEnterpriseBadgeCollections}
        returnFocusRef={returnFocusRef}
      />
    </TrackedDiv>
  );
};

export default CardDropdownWidget;
